import { useRef, useState, useCallback, useLayoutEffect } from 'react';
import type React from 'react';
import { getIframeDocumentContentHeightPx, settleIframeDocumentLayout } from '../utils/iframe';
import {
  getNodePage,
  isPageOverflowing,
  insertBlankDuplicatePageAfter,
  placeCaretInPageStub,
  reapplyPendingInsertion,
  isPageEffectivelyEmpty,
  placeCaretAtPageContentEnd,
  placeCaretAtPageContentStart,
  isCaretAtPageStart,
  isCaretAtPageEnd,
  getNextPageElement,
  getPreviousPageElement,
  getPagesStrictlyBetween,
  contentChildren,
  mutationRecordIsOnlyPageChrome,
  placeCaretForDesignModePage,
  normalizePageBreakSplitRangeStart,
  setIframeChromeRefreshActive,
  splitOverflowingPages,
} from '../utils/dom';

const FOOTER_COMPANY_NAME = 'CD CONSTRUCTION CONSULTING';
const FOOTER_COMPANY_ADDRESS = '13215 BEE CAVE PARKWAY, BLDG A, SUITE 130, AUSTIN, TEXAS 78738 512.476.3537  CDCC.COM';

interface UsePageManagerOptions {
  reportContentKey: string;
  syncState: () => void;
  getConfirmation: (props: {
    heading?: string;
    description?: string;
    confirmButton?: { label: string; [key: string]: unknown };
    cancelButton?: { label: string; [key: string]: unknown };
  }) => Promise<boolean>;
}

/**
 * Owns page-level operations: page markers (badges + separators), iframe height
 * management, page insert/delete, the MutationObserver, and all keyboard
 * boundary guards that prevent merging across page dividers.
 *
 * Receives `syncState` and `detectFooters` from sibling hooks via the main
 * component so that page operations can update formatting and footer state.
 */
export function usePageManager(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  { reportContentKey, syncState, getConfirmation }: UsePageManagerOptions,
) {
  const reflowObserverRef = useRef<MutationObserver | null>(null);
  const listenersRef = useRef<{
    doc: Document;
    handlers: Array<{ type: string; fn: EventListener; capture: boolean }>;
    timer: number | null;
  } | null>(null);
  const pageSepActionsRef = useRef<{
    insertAfterPageIndex: (idx: number) => void;
    deletePageAtIndex: (idx: number) => Promise<void>;
  }>({
    insertAfterPageIndex: () => undefined,
    deletePageAtIndex: async () => undefined,
  });
  const applyingPageMarkersRef = useRef(false);
  const lastIframeContentHeightRef = useRef<number | null>(null);
  const layoutGenerationRef = useRef(0);
  const [editorPageCount, setEditorPageCount] = useState(1);
  const [reportLayoutPending, setReportLayoutPending] = useState(true);

  useLayoutEffect(() => {
    layoutGenerationRef.current += 1;
    setReportLayoutPending(true);
  }, [reportContentKey]);

  const iDoc = useCallback(() => iframeRef.current?.contentDocument ?? null, [iframeRef]);
  const iWin = useCallback(() => iframeRef.current?.contentWindow ?? null, [iframeRef]);

  /** Re-render `__pg-badge__` / `__pg-sep__` elements and resize the iframe to content. */
  const refreshPageMarkers = useCallback(() => {
    const doc = iDoc();
    if (!doc) return;
    const win = doc.defaultView;
    setIframeChromeRefreshActive(win, true);
    applyingPageMarkersRef.current = true;
    try {
      doc.querySelectorAll('.__pg-sep__, .__pg-badge__, .__pg-footer__').forEach(el => el.remove());

      const pages = Array.from(doc.querySelectorAll('.page'));
      const total = pages.length;
      setEditorPageCount(total);

      pages.forEach((page, i) => {
        const badge = doc.createElement('div');
        badge.className = '__pg-badge__';
        badge.textContent = `Page ${i + 1} / ${total}`;
        page.appendChild(badge);

        if (!page.classList.contains('landscape')) {
          const footer = doc.createElement('div');
          footer.className = '__pg-footer__';
          footer.innerHTML = `<div class="footer-name">${FOOTER_COMPANY_NAME}</div>` + `<div>${FOOTER_COMPANY_ADDRESS}</div>`;
          (footer as HTMLElement).contentEditable = 'false';
          page.appendChild(footer);
        }

        if (i < total - 1) {
          const sep = doc.createElement('div');
          sep.className = '__pg-sep__';
          sep.setAttribute('data-after-page-index', String(i));
          (sep as HTMLElement).contentEditable = 'false';

          const label = doc.createElement('span');
          label.className = '__pg-sep__label';
          label.textContent = `\u2014 Page ${i + 1} / ${total} \u2014`;

          const insertBtn = doc.createElement('button');
          insertBtn.type = 'button';
          insertBtn.className = '__pg-sep__btn';
          insertBtn.setAttribute('data-pg-sep-action', 'insert-below');
          insertBtn.textContent = 'Insert page below';
          insertBtn.title = 'Insert a blank page below this page';
          insertBtn.contentEditable = 'false';

          const deleteBtn = doc.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = '__pg-sep__btn __pg-sep__btn--danger';
          deleteBtn.setAttribute('data-pg-sep-action', 'delete-page');
          deleteBtn.textContent = 'Delete this page';
          deleteBtn.title = 'Delete the page above this gap (page ' + String(i + 1) + ')';
          deleteBtn.contentEditable = 'false';

          sep.append(insertBtn, label, deleteBtn);
          page.after(sep);
        }
      });

      try {
        const h = getIframeDocumentContentHeightPx(doc);
        if (h && iframeRef.current) {
          const next = h + 40;
          const prev = lastIframeContentHeightRef.current;
          if (prev == null || Math.abs(prev - next) > 2) {
            iframeRef.current.style.height = `${next}px`;
            lastIframeContentHeightRef.current = next;
          }
        }
      } catch {
        /* ignore */
      }
    } finally {
      applyingPageMarkersRef.current = false;
      setIframeChromeRefreshActive(win, false);
    }
  }, [iDoc, iframeRef]);

  /** Split the current page at the caret, moving trailing content to a new page. */
  const insertPageBreak = useCallback(() => {
    const doc = iDoc();
    const win = iWin();
    if (!doc || !win) return;

    const sel = win.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);

    const startNode = range.startContainer;
    const node: Element | null = startNode.nodeType === Node.TEXT_NODE ? (startNode as Text).parentElement : (startNode as Element);

    let currentPage: Element | null = null;
    let temp: Element | null = node;
    while (temp && temp !== doc.documentElement) {
      if (temp.classList?.contains('page')) {
        currentPage = temp;
        break;
      }
      temp = temp.parentElement;
    }
    if (!currentPage) return;

    // Prevent splitting if caret is inside page chrome.
    let checkNode: Element | null = node;
    while (checkNode && checkNode !== currentPage) {
      const cls = checkNode.className || '';
      if (cls.includes('__pg-badge__') || cls.includes('__pg-sep__')) return;
      checkNode = checkNode.parentElement;
    }

    const newPage = doc.createElement('div');
    newPage.className = currentPage.className;

    const style = currentPage.getAttribute('style');
    if (style) newPage.setAttribute('style', style);

    const isChrome = (n: Node): boolean => {
      if (n.nodeType !== Node.ELEMENT_NODE) return false;
      const cls = (n as Element).className || '';
      return cls.includes('__pg-badge__') || cls.includes('__pg-sep__');
    };

    const contentNodes = Array.from(currentPage.childNodes).filter(n => !isChrome(n));
    const lastContent = contentNodes[contentNodes.length - 1];

    const splitRange = doc.createRange();
    splitRange.setStart(range.startContainer, range.startOffset);
    normalizePageBreakSplitRangeStart(splitRange, currentPage);
    if (lastContent) {
      splitRange.setEndAfter(lastContent);
    } else {
      return;
    }

    currentPage.after(newPage);

    if (splitRange.collapsed) {
      const stub = doc.createElement('p');
      stub.innerHTML = '<br>';
      newPage.appendChild(stub);
    } else {
      try {
        newPage.appendChild(splitRange.extractContents());
      } catch {
        newPage.remove();
        return;
      }
    }

    if (contentChildren(newPage).length === 0) {
      const stub = doc.createElement('p');
      stub.innerHTML = '<br>';
      newPage.insertBefore(stub, newPage.firstChild);
    }

    if (isPageEffectivelyEmpty(currentPage)) {
      contentChildren(currentPage).forEach(k => k.remove());
      const stub = doc.createElement('p');
      stub.innerHTML = '<br>';
      currentPage.insertBefore(stub, currentPage.firstChild);
    }

    refreshPageMarkers();

    newPage.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    iframeRef.current?.contentWindow?.focus();
    placeCaretForDesignModePage(newPage, doc, win);
    syncState();
  }, [iDoc, iWin, iframeRef, refreshPageMarkers, syncState]);

  /** Remove the page the caret is on (requires at least two pages). */
  const deleteCurrentPage = useCallback(async () => {
    const doc = iDoc();
    const win = iWin();
    if (!doc || !win) return;

    const pages = Array.from(doc.querySelectorAll('.page'));
    if (pages.length <= 1) return;

    const sel = win.getSelection();
    if (!sel?.rangeCount) return;

    const startNode = sel.getRangeAt(0).startContainer;
    const node: Node | null = startNode.nodeType === Node.TEXT_NODE ? (startNode as Text).parentElement : startNode;
    const page = getNodePage(node ?? doc.body, doc);
    if (!page) return;

    const idx = pages.indexOf(page);
    if (idx < 0) return;

    const bodyKids = contentChildren(page);
    if (bodyKids.length > 0) {
      const confirmed = await getConfirmation({
        heading: 'Delete Page',
        description: 'Delete this page and all content on it?',
        confirmButton: { label: 'Delete', color: 'error' },
        cancelButton: { label: 'Cancel', color: 'secondary' },
      });
      if (!confirmed) return;
    }

    const focusPage = idx > 0 ? pages[idx - 1] : pages[idx + 1];
    page.remove();
    refreshPageMarkers();
    placeCaretAtPageContentStart(focusPage, doc, win);
    syncState();
  }, [iDoc, iWin, refreshPageMarkers, syncState, getConfirmation]);

  /** Insert a blank duplicate page after the page at `pageIndex` (0-based). */
  const insertPageAfterPageIndex = useCallback(
    (pageIndex: number) => {
      const doc = iDoc();
      const win = iWin();
      if (!doc || !win) return;
      const pages = Array.from(doc.querySelectorAll('.page'));
      const source = pages[pageIndex];
      if (!source) return;
      const newPage = insertBlankDuplicatePageAfter(source as HTMLElement, doc);
      refreshPageMarkers();
      newPage.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      iframeRef.current?.contentWindow?.focus();
      placeCaretInPageStub(newPage, doc, win);
      syncState();
    },
    [iDoc, iWin, iframeRef, refreshPageMarkers, syncState],
  );

  /** Remove the page at `pageIndex` (0-based). */
  const deletePageAtIndex = useCallback(
    async (pageIndex: number) => {
      const doc = iDoc();
      const win = iWin();
      if (!doc || !win) return;
      const pages = Array.from(doc.querySelectorAll('.page'));
      if (pages.length <= 1) return;
      const page = pages[pageIndex];
      if (!page) return;

      const bodyKids = contentChildren(page);
      if (bodyKids.length > 0) {
        const confirmed = await getConfirmation({
          heading: 'Delete Page',
          description: 'Delete this page and all content on it?',
          confirmButton: { label: 'Delete', color: 'error' },
          cancelButton: { label: 'Cancel', color: 'secondary' },
        });
        if (!confirmed) return;
      }

      const focusPage = pageIndex > 0 ? pages[pageIndex - 1] : pages[pageIndex + 1];
      page.remove();
      refreshPageMarkers();
      if (focusPage?.isConnected) placeCaretAtPageContentStart(focusPage, doc, win);
      syncState();
    },
    [iDoc, iWin, refreshPageMarkers, syncState, getConfirmation],
  );

  useLayoutEffect(() => {
    pageSepActionsRef.current.insertAfterPageIndex = insertPageAfterPageIndex;
    pageSepActionsRef.current.deletePageAtIndex = deletePageAtIndex;
  }, [insertPageAfterPageIndex, deletePageAtIndex]);

  /**
   * `onLoad` handler for the iframe — sets up all event listeners,
   * the MutationObserver, and the initial page-marker pass.
   */
  const handleLoad = useCallback(() => {
    const loadToken = ++layoutGenerationRef.current;
    try {
      reflowObserverRef.current?.disconnect();
      reflowObserverRef.current = null;
      if (listenersRef.current) {
        const prev = listenersRef.current;
        prev.handlers.forEach(({ type, fn, capture }) => prev.doc.removeEventListener(type, fn, capture));
        if (prev.timer != null) prev.doc.defaultView?.clearTimeout(prev.timer);
        listenersRef.current = null;
      }

      const doc = iDoc();
      if (!doc) {
        setReportLayoutPending(false);
        return;
      }
      const win = iframeRef.current?.contentWindow;

      // Initial iframe height
      lastIframeContentHeightRef.current = null;
      try {
        const h = getIframeDocumentContentHeightPx(doc);
        if (h && iframeRef.current) {
          const next = h + 40;
          iframeRef.current.style.height = `${next}px`;
          lastIframeContentHeightRef.current = next;
        }
      } catch {
        /* ignore */
      }

      const sessionListeners: Array<{ type: string; fn: EventListener; capture: boolean }> = [];
      const session = { doc, handlers: sessionListeners, timer: null as number | null };
      listenersRef.current = session;

      const addListener = (type: string, fn: EventListener, capture = false): void => {
        doc.addEventListener(type, fn, capture);
        sessionListeners.push({ type, fn, capture });
      };

      addListener('selectionchange', syncState as EventListener);
      addListener('keyup', syncState as EventListener);

      if (!win) {
        setReportLayoutPending(false);
        return;
      }

      let pendingInsertPage: Element | null = null;
      let pendingInsertAttempt = false;
      let pendingInsertWasOverflowing = false;
      let pendingInsertInTable = false;
      let pendingInputSnapshot: { inputType: string; data: string | null } | null = null;
      let pendingPasteSnapshot: { html: string; text: string } | null = null;
      let composing = false;

      const onCompositionStart: EventListener = () => {
        composing = true;
      };
      const onCompositionEnd: EventListener = () => {
        composing = false;
      };

      const doLockAndRefresh = () => {
        refreshPageMarkers();
      };

      const onPaste: EventListener = e => {
        const pe = e as ClipboardEvent;
        pendingPasteSnapshot = {
          text: pe.clipboardData?.getData('text/plain') ?? '',
          html: pe.clipboardData?.getData('text/html') ?? '',
        };
      };

      // Capture page + overflow state *before* the insert lands in the DOM.
      const onBeforeInput: EventListener = e => {
        const bie = e as InputEvent;
        if (!bie.inputType?.startsWith('insert')) return;
        if (bie.inputType !== 'insertFromPaste') pendingPasteSnapshot = null;
        pendingInsertAttempt = true;
        pendingInputSnapshot = { inputType: bie.inputType, data: bie.data };
        pendingInsertPage = null;
        pendingInsertWasOverflowing = false;
        pendingInsertInTable = false;
        const sel = win.getSelection();
        if (!sel?.rangeCount) return;
        const biStart = sel.getRangeAt(0).startContainer;
        const biNode: Element | null = biStart.nodeType === Node.TEXT_NODE ? (biStart as Text).parentElement : (biStart as Element);
        pendingInsertPage = getNodePage(biNode ?? doc.body, doc);
        pendingInsertWasOverflowing = pendingInsertPage ? isPageOverflowing(pendingInsertPage, win) : false;
        // Never auto-break a page when typing inside a table — content would be
        // torn out of the cell and replayed on a blank page, corrupting the table.
        pendingInsertInTable = !!biNode?.closest?.('table');
      };

      // If the insert caused overflow: undo it, open a new page, replay the insert.
      // Skipped when the caret is inside a table (tables must grow freely).
      const onInput: EventListener = () => {
        if (composing || !pendingInsertAttempt) return;
        const page = pendingInsertPage;
        const wasOverflowing = pendingInsertWasOverflowing;
        const inTable = pendingInsertInTable;
        const inputSnap = pendingInputSnapshot;
        const pasteSnap = pendingPasteSnapshot;
        pendingInsertAttempt = false;
        pendingInsertPage = null;
        pendingInsertWasOverflowing = false;
        pendingInsertInTable = false;
        pendingInputSnapshot = null;
        pendingPasteSnapshot = null;
        if (!page || wasOverflowing || inTable || !isPageOverflowing(page, win)) return;
        doc.execCommand('undo', false);
        const newPage = insertBlankDuplicatePageAfter(page as HTMLElement, doc);
        placeCaretInPageStub(newPage, doc, win);
        reapplyPendingInsertion(doc, inputSnap, pasteSnap);
        refreshPageMarkers();
        syncState();
      };

      // Guard Backspace/Delete so they can't merge pages or destroy page structure.
      // Skip entirely when the caret is inside a table — cell navigation must stay free.
      const onKeyDown: EventListener = e => {
        const ke = e as KeyboardEvent;
        if (ke.key !== 'Backspace' && ke.key !== 'Delete') return;
        const sel = win.getSelection();
        if (!sel?.rangeCount) return;
        const range = sel.getRangeAt(0);
        const kdStart = range.startContainer;
        const node: Element | null = kdStart.nodeType === Node.TEXT_NODE ? (kdStart as Text).parentElement : (kdStart as Element);
        if (node?.closest?.('table')) return;
        const page = getNodePage(node ?? doc.body, doc);
        if (!page) return;

        const previousPage = getPreviousPageElement(page);
        const nextPage = getNextPageElement(page);

        const startPg = getNodePage(range.startContainer, doc);
        const endPg = getNodePage(range.endContainer, doc);

        if (ke.key === 'Backspace' && !range.collapsed && startPg && endPg && startPg !== endPg) {
          const gapPages = getPagesStrictlyBetween(doc, startPg, endPg);
          const emptyGap = gapPages.filter(p => isPageEffectivelyEmpty(p));
          if (emptyGap.length > 0) {
            ke.preventDefault();
            for (let i = 0; i < emptyGap.length; i++) {
              const gp = emptyGap[i];
              if (!gp.isConnected) continue;
              if (doc.querySelectorAll('.page').length <= 1) break;
              if (isPageEffectivelyEmpty(gp)) gp.remove();
            }
            try {
              range.deleteContents();
            } catch {
              /* ignore */
            }
            if (endPg.isConnected && isPageEffectivelyEmpty(endPg) && doc.querySelectorAll('.page').length > 1) {
              endPg.remove();
            }
            if (startPg.isConnected) placeCaretAtPageContentEnd(startPg, doc, win);
            else {
              const first = doc.querySelector('.page');
              if (first) placeCaretAtPageContentStart(first, doc, win);
            }
            refreshPageMarkers();
            syncState();
            return;
          }
        }

        if (
          ke.key === 'Backspace' &&
          startPg &&
          endPg &&
          startPg !== endPg &&
          isPageEffectivelyEmpty(endPg) &&
          getPreviousPageElement(endPg) === startPg
        ) {
          ke.preventDefault();
          const emptyPageEl = endPg;
          try {
            range.deleteContents();
          } catch {
            /* ignore */
          }
          if (doc.querySelectorAll('.page').length > 1 && emptyPageEl.isConnected && isPageEffectivelyEmpty(emptyPageEl)) {
            emptyPageEl.remove();
          }
          placeCaretAtPageContentEnd(startPg, doc, win);
          refreshPageMarkers();
          syncState();
          return;
        }

        if (ke.key === 'Backspace' && previousPage && isPageEffectivelyEmpty(page)) {
          ke.preventDefault();
          if (doc.querySelectorAll('.page').length > 1) page.remove();
          placeCaretAtPageContentEnd(previousPage, doc, win);
          refreshPageMarkers();
          syncState();
          return;
        }
        if (ke.key === 'Backspace' && previousPage && isCaretAtPageStart(range, page)) {
          ke.preventDefault();
          placeCaretAtPageContentEnd(previousPage, doc, win);
          syncState();
          return;
        }
        if (ke.key === 'Delete' && nextPage && isCaretAtPageEnd(range, page)) {
          ke.preventDefault();
        }
      };

      const onPageSeparatorClick: EventListener = e => {
        const t = e.target as Node | null;
        if (!t || t.nodeType !== Node.ELEMENT_NODE) return;
        const btn = (t as Element).closest('[data-pg-sep-action]');
        if (!btn) return;
        const sep = btn.closest('.__pg-sep__');
        if (!sep) return;
        e.preventDefault();
        e.stopPropagation();
        const raw = sep.getAttribute('data-after-page-index');
        const idx = raw == null ? -1 : parseInt(raw, 10);
        if (!Number.isFinite(idx) || idx < 0) return;
        const action = btn.getAttribute('data-pg-sep-action');
        if (action === 'insert-below') {
          pageSepActionsRef.current.insertAfterPageIndex(idx);
        } else if (action === 'delete-page') {
          void pageSepActionsRef.current.deletePageAtIndex(idx);
        }
      };

      addListener('compositionstart', onCompositionStart);
      addListener('compositionend', onCompositionEnd);
      addListener('keyup', doLockAndRefresh as EventListener);
      addListener('paste', onPaste);
      addListener('beforeinput', onBeforeInput);
      addListener('input', onInput);
      addListener('keydown', onKeyDown);
      addListener('click', onPageSeparatorClick, true);

      // Re-run page markers on any meaningful DOM mutation (debounced).
      const obs = new MutationObserver(mutations => {
        if (applyingPageMarkersRef.current) return;
        const relevant = mutations.some(m => !mutationRecordIsOnlyPageChrome(m));
        if (!relevant) return;
        win.clearTimeout(session.timer ?? undefined);
        session.timer = win.setTimeout(doLockAndRefresh, 100);
      });

      obs.observe(doc.body, { childList: true, subtree: true, characterData: true });
      reflowObserverRef.current = obs as MutationObserver;
      refreshPageMarkers();

      void (async () => {
        try {
          await settleIframeDocumentLayout(doc);
          if (loadToken !== layoutGenerationRef.current) return;
          if (win) splitOverflowingPages(doc, win);
          refreshPageMarkers();
          setReportLayoutPending(false);
        } catch {
          if (loadToken === layoutGenerationRef.current) setReportLayoutPending(false);
        }
      })();
    } catch {
      setReportLayoutPending(false);
    }
  }, [iDoc, iframeRef, syncState, refreshPageMarkers]);

  return { insertPageBreak, deleteCurrentPage, editorPageCount, handleLoad, reportLayoutPending };
}
